import superjson from 'superjson';
import { bulkCreateFiles } from '../helpers/chatApiClient'; // Zod-free API client

// Avoid repeated 404 spam for missing previews in short bursts.
const missingPreviewCache = new Map<string, number>();
const MISSING_PREVIEW_TTL_MS = 60_000;

/** Parse a response that could be superjson or plain JSON */
async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  try {
    return superjson.parse<T>(text);
  } catch {
    return JSON.parse(text) as T;
  }
}

export async function getPreview(
  sessionId: string,
): Promise<{ sessionId: string; previewSubdomain: string; previewUrl: string } | null> {
  const cachedMissingAt = missingPreviewCache.get(sessionId);
  if (cachedMissingAt && Date.now() - cachedMissingAt < MISSING_PREVIEW_TTL_MS) {
    return null;
  }
  const res = await fetch(`/_api/vibes/${encodeURIComponent(sessionId)}/preview`);
  if (res.status === 404) {
    missingPreviewCache.set(sessionId, Date.now());
    return null;
  }
  if (!res.ok) {
    const err = await parseResponse<{ error?: string }>(res).catch(() => null);
    throw new Error(err?.error || `Failed to get preview: ${res.status}`);
  }
  missingPreviewCache.delete(sessionId);
  return parseResponse(res);
}

export async function createPreview(
  sessionId: string,
): Promise<{ sessionId: string; previewSubdomain: string; previewUrl: string }> {
  const res = await fetch(`/_api/vibes/create-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ sessionId }),
  });
  if (!res.ok) {
    const err = await parseResponse<{ error?: string }>(res).catch(() => null);
    throw new Error(err?.error || `Failed to create preview: ${res.status}`);
  }
  return parseResponse(res);
}

export async function publishPreview(sessionId: string): Promise<void> {
  const res = await fetch(`/_api/vibes/${encodeURIComponent(sessionId)}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({}),
  });
  if (!res.ok) {
    const err = await parseResponse<{ error?: string }>(res).catch(() => null);
    throw new Error(err?.error || `Failed to publish preview: ${res.status}`);
  }
}

// Convenience alias for clarity when publishing Rust previews from UI
export async function publishRustPreview(sessionId: string): Promise<void> {
  return publishPreview(sessionId);
}

export async function seedRustWasmFiles(sessionId: string): Promise<void> {
  const files = [
    {
      filePath: '/',
      fileName: 'index.html',
      content: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Rust Wasm SPA</title>
</head>
<body>
  <div id="app"></div>
  <script type="module" src="./index.js"></script>
</body>
</html>`,
    },
    {
      filePath: '/',
      fileName: 'index.js',
      content: `import init from './pkg/app.js';
init().then(() => {
  console.log('Wasm initialized');
});`,
    },
    {
      filePath: '/pkg',
      fileName: 'app.js',
      content: `// Placeholder wasm-bindgen glue. Replaced after Rust build publish.
export default async function init() {
  console.warn('Rust WASM bundle not compiled yet. Publish/build to generate real pkg artifacts.');
}`,
    },
    {
      filePath: '/pkg',
      fileName: 'app_bg.wasm',
      content: 'placeholder', // TODO: Replace with actual compiled Wasm binary
    },
  ];

  await bulkCreateFiles({ sessionId, files });
}
